import { useState, useEffect, useCallback, useRef } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useSearchParams } from "react-router-dom";
import { AuthProvider, useAuth } from './context/AuthContext';
import { OnlineProvider } from './context/OnlineContext';
import OfflineIndicator from './components/OfflineIndicator';
import ScrollToTop from './components/ScrollToTop';
import AuthPage from './components/AuthPage';
import HUDLayout from './components/HUDLayout';
import TelephoneGame from './components/TelephoneGame';
import MyRootsDemo from './components/MyRootsDemo';
import AIGuide from './components/AIGuide';
import AudioHUD from './components/AudioHUD';
import WorkCenter from './components/WorkCenter';
import AdminPanel from './components/AdminPanel';
import Subscription from './components/Subscription';
import SubscriptionSuccess from './components/SubscriptionSuccess';
import AccountabilityPage from './components/AccountabilityPage';
import MethodologyModerationPage from './components/MethodologyModerationPage';
import MyProfileDemo from './components/MyProfileDemo';
import ParentalControls from './components/ParentalControls';
import ScopedGeoView from './components/ScopedGeoView';
import ReviewQueue from './components/ReviewQueue';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import VerifyEmail from './components/VerifyEmail';
import TeacherVerify from './components/TeacherVerify';
import TeacherApply from './components/TeacherApply';
import LessonsContest from './components/LessonsContest';
import IntegrityDashboard from './components/IntegrityDashboard';
import MyCertificate from './components/MyCertificate';
import AboutPage from './components/AboutPage';
import ContactPage from './components/ContactPage';
import CertificateVerify from './components/CertificateVerify';
import TeacherProfile from './components/TeacherProfile';
import PipelineQueue from './components/PipelineQueue';
import PipelineOpsDashboard from './components/PipelineOpsDashboard';
import OpeningCinematic from './components/OpeningCinematic';
import FounderOpening, { markCinematicSeen } from './components/FounderOpening';
import PublicLanding from './components/PublicLanding';
import DemoChainPage from './components/DemoChainPage';
import TheFirstWalker from './components/TheFirstWalker';
import TheShapedStone from './components/TheShapedStone';
import MyForks from './components/MyForks';
import ForkEditor from './components/ForkEditor';
import BillingPage from './components/BillingPage';
import BillingSuccess from './components/BillingSuccess';
import BillingCancel from './components/BillingCancel';
import LibrarianMentor from './components/LibrarianMentor';
import api, { globeAPI, chainAPI, familyAPI, lineageAPI } from './lib/api';
import { resolveTimelineScope } from './lib/countryFoundings';
import { getEraForYear } from './lib/eraGrammar';
import TeacherChat from './components/TeacherChat';
import ContentPage from './components/ContentPage';
import LaunchBanner from './components/LaunchBanner';
import WhatsNewTicker from './components/WhatsNewTicker';

// Activeview names that get their own shareable URL path (single-segment).
// Includes profile/roots/about/contact/etc. so judges and users can copy
// /profile or /about and land on the right pane after auth. Geographic
// scope routes (`/[country]/...`) always take precedence; these single-
// segment paths only fire when no country scope is active.
const SHAREABLE_VIEW_PATHS = new Set([
  'profile', 'roots', 'my-certificate', 'about', 'contact',
  'workcenter', 'accountability', 'methodology-moderation',
  'admin', 'pipeline-queue', 'pipeline-ops', 'review-queue',
  'teacher-apply', 'lessons-contest', 'integrity-dashboard',
]);

/* ─── Subject pages with chain list ─── */
function SubjectPage({ subject, isKidMode, onChainSelect }) {
  const subjects = {
    world: {
      title: 'World',
      desc: "Trace how the world got the way it is. Origins, contradictions, and the patterns that keep showing up.",
      kidDesc: "See how people lived all over the planet — and how those stories travel.",
    },
    us: {
      title: 'United States',
      desc: "The American chain — starting with the people who were already here. Documented sources. Contradictions kept honest.",
      kidDesc: "America's story, with all the parts. Sometimes proud. Sometimes hard. Always real.",
    },
    state: {
      title: 'Your State',
      desc: "Your state's chain — how it actually got built, with sources you can check yourself.",
      kidDesc: "Your state has its own chain of stories. Find out where it really comes from.",
    },
    city: {
      title: 'Your City',
      desc: "Whatever happened on these streets before the buildings you walk past — that's the chain.",
      kidDesc: "Your city is older than it looks. Find out what was here before.",
    },
    power: {
      title: 'Power & Structures',
      desc: "Who decided. Who profited. Who paid. The patterns are surprisingly consistent.",
      kidDesc: "Who's in charge — kings, presidents, councils — and why that matters for everyone else.",
    },
    bios: {
      title: 'Biographies',
      desc: "People history actually swings on. Including the ones the official textbooks left out.",
      kidDesc: "The people who shaped how things turned out. Some famous. Some you've never heard of.",
    },
    weird: {
      title: 'Weird Facts',
      desc: "Documented oddities. Pattern-matched across cultures. Cited.",
      kidDesc: "Did you know Romans used PEE as mouthwash?! Yeah, history is WILD.",
    },
    monuments: {
      title: 'Monuments & Symbols',
      desc: "Statues, seals, flags, buildings — what they were built to say, and what they say now once you trace the chain.",
      kidDesc: "Big statues. Hidden symbols. Buildings older than countries. Each one has a chain.",
    },
    profile: {
      title: 'My Profile',
      desc: "Your account. Your data. Your call.",
      kidDesc: "This is YOUR space. Set up your time machine the way you like it!",
    },
    parental: {
      title: 'Parental Controls',
      desc: "Set the PIN, filter content by era and subject, decide what your kid sees. Your account, your call.",
      kidDesc: "This section is for grown-ups only.",
    },
    about: {
      title: 'About Us',
      desc: "TSOGN is built by Midnite Productions LLC. The methodology — sources, contradictions, no narration — is the platform. Subscription as the model. Privacy follows.",
      kidDesc: "We built this so you can explore history honestly — with sources, not slogans.",
    },
    contact: {
      title: 'Contact Us',
      desc: "Got a source we missed? A correction? A real concern? Write us. A real human reads it.",
      kidDesc: "Got a question? A cool history fact? Tell us!",
    },
  };
  const s = subjects[subject] || subjects.world;

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid={`subject-${subject}`}>
      <h2 className="font-historical text-[#D4A84A] text-3xl md:text-4xl lg:text-5xl font-light tracking-tight mb-3">
        {s.title}
      </h2>
      <p className="font-mono text-sm md:text-base lg:text-lg text-[#D4D4D8] leading-relaxed mb-6 max-w-3xl">
        {isKidMode ? s.kidDesc : s.desc}
      </p>
      {subject === 'world' && (
        <div className="mb-4 max-w-3xl" data-testid="world-whats-new">
          <WhatsNewTicker compact />
        </div>
      )}
      <TelephoneGame onNodeSelect={() => {}} onChainSelect={onChainSelect} />
    </div>
  );
}

function MainApp() {
  const { user, loading } = useAuth();
  const [activeView, setActiveView] = useState('world');
  const [globeNodes, setGlobeNodes] = useState([]);
  const [globeBeacons, setGlobeBeacons] = useState([]);
  // Apr 28 2026 brief: the timeline starts 7 million years ago. The gold
  // cursor (and the play-button anchored to it) initialises at the very
  // origin of the human story. Updates as the user scrubs / walks chains.
  const [currentYear, setCurrentYear] = useState(-7000000);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [timelineScope, setTimelineScope] = useState(null);
  // Default to ADULT mode. Lead methodology: clearer head than they came in with —
  // adults defaulting to kid mode adds cognitive friction. Parents flip kid mode ON
  // for their kids' accounts; that's an opt-in safety move, not a default. PIN
  // protection on kid→adult flip still applies when a real PIN is set.
  const [isKidMode, setIsKidMode] = useState(false);
  const [soundOn, setSoundOn] = useState(true);

  // Sync SFX enabled state with HUD toggle
  useEffect(() => {
    // Dynamic import avoids SSR concerns and keeps the bundle leaner if unused
    import('./lib/sfx').then((sfx) => sfx.setSfxEnabled(soundOn)).catch(() => {});
  }, [soundOn]);

  // Unlock on first user gesture (autoplay policy)
  useEffect(() => {
    const unlock = () => {
      import('./lib/sfx').then((sfx) => sfx.unlockAudio()).catch(() => {});
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  // Boot Google Translate state if user has a non-English language set
  useEffect(() => {
    import('./lib/translate').then((mod) => mod.initOnBoot()).catch(() => {});
  }, []);

  const [activeChain, setActiveChain] = useState(null);
  const [scope, setScope] = useState({ country: null, subdivision: null, city: null });

  // URL deep-link routing — Foundation Brief §1: bookmarks & shared links
  // restore exact scope. Format:
  //   /world
  //   /[country-slug]/history
  //   /[country-slug]/[subdivision-slug]
  //   /[country-slug]/[subdivision-slug]/[city-slug]
  //
  // Plus single-segment view paths (added Apr 29 2026 — share-friendly):
  //   /profile, /roots, /my-certificate, /about, /contact, /workcenter,
  //   /accountability, /methodology-moderation, /admin, /pipeline-queue,
  //   /pipeline-ops, /review-queue, /teacher-apply, /lessons-contest,
  //   /integrity-dashboard
  //
  // Parsed on first load and whenever the user lands via popstate; written
  // back when the cascading nav or active view changes.
  useEffect(() => {
    const path = window.location.pathname.replace(/^\/+|\/+$/g, '');
    if (!path || path === 'world') return;
    const parts = path.split('/').filter(Boolean);
    if (parts.length === 0) return;

    // Single-segment activeView paths take precedence over scope parsing
    // when the segment matches a known view name.
    if (parts.length === 1 && SHAREABLE_VIEW_PATHS.has(parts[0])) {
      setActiveView(parts[0]);
      return;
    }

    import('./lib/api').then(({ default: api }) => {
      const params = new URLSearchParams();
      params.set('country_slug', parts[0]);
      if (parts.length === 2 && parts[1] !== 'history') params.set('subdivision_slug', parts[1]);
      if (parts.length >= 3) {
        params.set('subdivision_slug', parts[1]);
        params.set('city_slug', parts[2]);
      }
      api.get(`/geo/scope?${params.toString()}`).then((r) => {
        const next = { country: r.data?.country || null, subdivision: r.data?.subdivision || null, city: r.data?.city || null };
        setScope(next);
        if (next.city?.chain_status === 'published') setActiveView('city');
        else if (next.subdivision?.chain_status === 'published') setActiveView('subdivision');
        else if (next.country?.chain_status === 'published') setActiveView('country');
      }).catch(() => {});
    });
  }, []);

  // Reflect scope OR activeView back to URL when either changes (no reload).
  // Geographic scope paths only win when the user is actually looking at a
  // geographic view (world / country / subdivision / city). When the user
  // is on Profile / Roots / About / Contact / etc., the activeView path
  // wins so the share-friendly URL is preserved in the address bar.
  useEffect(() => {
    const isGeoView = ['world', 'country', 'subdivision', 'city'].includes(activeView);
    let url = '/';
    if (SHAREABLE_VIEW_PATHS.has(activeView)) {
      url = `/${activeView}`;
    } else if (isGeoView && scope.country?.slug) {
      url = `/${scope.country.slug}`;
      if (scope.subdivision?.slug) {
        url += `/${scope.subdivision.slug}`;
        if (scope.city?.slug) url += `/${scope.city.slug}`;
      } else {
        url += '/history';
      }
    } else if (activeView === 'world') {
      url = '/world';
    }
    if (window.location.pathname !== url) {
      window.history.replaceState({}, '', url);
    }
  }, [scope, activeView]);

  // Era grammar: track active era based on lesson being viewed
  const activeEra = activeChain?.origin?.year ? getEraForYear(activeChain.origin.year) : null;

  // Era transition chord — fires when activeEra changes to a new lesson era
  const prevEraRef = useRef(null);
  useEffect(() => {
    if (activeEra && activeEra !== prevEraRef.current) {
      import('./lib/sfx').then((sfx) => sfx.playEraShift()).catch(() => {});
    }
    prevEraRef.current = activeEra;
  }, [activeEra]);

  const fetchData = useCallback(async () => {
    try {
      const [nodesRes, chainsRes] = await Promise.all([
        globeAPI.nodes(),
        chainAPI.list(),
      ]);
      setGlobeNodes(nodesRes.data);

      // Beacons include journey_points for cinematic flight-path rendering.
      // Fails silently if lineage_visible=false (user hasn't opted in).
      lineageAPI.beacons().then((r) => {
        const payload = r.data || {};
        if (payload.lineage_visible && Array.isArray(payload.beacons)) {
          setGlobeBeacons(payload.beacons);
        } else {
          setGlobeBeacons([]);
        }
      }).catch(() => setGlobeBeacons([]));

      const events = [];
      chainsRes.data.forEach(chain => {
        ['origin', 'change_a', 'change_b', 'modern_pattern'].forEach(key => {
          const step = chain[key];
          if (step?.year) {
            events.push({
              year: step.year,
              label: step.title,
              type: 'chain',
              chainId: chain.id,
              chainTitle: chain.title,
              chainEra: chain.era,
              chainDescription: chain.description,
              chainPicture: chain.pinhole_opening_url,
              stepKey: key,
              stepLocation: step.location,
              lat: step.lat,
              lng: step.lng,
            });
          }
        });
        if (chain.has_contradiction && chain.contradiction?.year) {
          events.push({
            year: chain.contradiction.year,
            label: chain.contradiction.title,
            type: 'chain',
            chainId: chain.id,
            chainTitle: chain.title,
            chainEra: chain.era,
            chainDescription: chain.description,
            chainPicture: chain.pinhole_opening_url,
            stepKey: 'contradiction',
          });
        }
      });

      // Family events + /timeline/events context dots REMOVED from the
      // timeline Apr 30 2026 iter_62 per Lead: "timeline needs to clear
      // all chains. Except the real." The timeline now shows ONLY the
      // 4 canonical episode markers. DO NOT re-add the 32 deep-time
      // context events or the family-birth markers without the Lead's
      // explicit go-ahead.

      setTimelineEvents(events.sort((a, b) => a.year - b.year));
    } catch {}
  }, []);

  useEffect(() => {
    if (user && user !== false) fetchData();
  }, [user, fetchData]);

  // Per-tab timeline scope — DISABLED Apr 30 2026 iter_62 per Lead:
  // "Timeline shouls start at the beginning not the middle." The iter_46
  // country-founding snap was putting the user in the middle of modern
  // history (e.g. US=1492 CE) instead of the start of the deep-time arc
  // where the 4 canonical episodes live. The timeline now always shows
  // the full -7,000,000 → 2026 span on every tab. Country / city scope
  // still drives the cascade dropdowns but NO LONGER moves the timeline.
  useEffect(() => {
    setTimelineScope(null);
    if (currentYear > -1000000) {
      setCurrentYear(-7000000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope?.country?.slug, scope?.subdivision?.slug, scope?.city?.slug]);

  // Listen for global "open profile" intent (e.g., from OfflineIndicator failed-sync badge)
  useEffect(() => {
    const handler = () => { setActiveChain(null); setActiveView('profile'); };
    window.addEventListener('tsogn:open-profile', handler);
    return () => window.removeEventListener('tsogn:open-profile', handler);
  }, []);

  // Listen for chat → lessons-contest hand-off
  useEffect(() => {
    const handler = () => { setActiveChain(null); setActiveView('lessons-contest'); };
    window.addEventListener('tsogn:open-lessons-contest', handler);
    return () => window.removeEventListener('tsogn:open-lessons-contest', handler);
  }, []);

  // ─── FOLLOW-THIS-ANCESTOR — three surfaces threaded by one event ───
  // 1) App scrubs the timeline to the ancestor's birth year (currentYear).
  // 2) After a brief settle pause (so the year-driven globe rotation lands
  //    on the framing target first), it re-dispatches a playback event the
  //    MiniGlobe is listening for, which kicks off the cinematic replay.
  // 3) MyRoots reveals the source-driven JourneyNarrativePane in the same
  //    component tree where the click originated. (Methodology hold #5: the
  //    platform surfaces the user's data; it doesn't narrate it.)
  useEffect(() => {
    const onFollow = (e) => {
      const { beaconId, birth_year } = e.detail || {};
      if (Number.isFinite(birth_year)) {
        setCurrentYear(birth_year);
      }
      if (beaconId) {
        // Settle pause — let the timeline-driven globe rotation finish
        // its lerp before scripted playback grabs the rotation target.
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('tsogn:trigger-playback', { detail: { beaconId } }));
        }, 700);
      }
    };
    window.addEventListener('tsogn:follow-ancestor', onFollow);
    return () => window.removeEventListener('tsogn:follow-ancestor', onFollow);
  }, []);

  // ─── Lineage Digest deep-link: /<anything>?follow=ancestorId ───
  // When a Lineage Digest email or shared URL contains ?follow=, look the
  // ancestor up via the existing roots API and dispatch the same event that
  // MyRoots's "Follow this ancestor" button fires. This lets a recipient drop
  // straight from inbox/tweet into the source-driven cinematic replay
  // without manually navigating the My Roots tree.
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    const followId = params.get('follow');
    if (!followId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await familyAPI.list();
        const member = (r.data || []).find((m) => m.id === followId);
        if (!member || cancelled) return;
        // Navigate into the My Roots view first so the JourneyNarrativePane
        // has a host component, then dispatch the event after one tick.
        setActiveView('roots');
        setTimeout(() => {
          if (cancelled) return;
          window.dispatchEvent(new CustomEvent('tsogn:follow-ancestor', {
            detail: { beaconId: member.beacon_id, birth_year: member.birth_year, memberId: member.id },
          }));
          // Strip the query param so a refresh doesn't re-fire
          const url = new URL(window.location.href);
          url.searchParams.delete('follow');
          window.history.replaceState({}, '', url.toString());
        }, 250);
      } catch {/* member not found / not authed → silent */}
    })();
    return () => { cancelled = true; };
  }, [user]);

  const handleNodeClick = (node) => {
    if (node.chain_id) setActiveView('world');
  };

  const handleYearChange = (year) => setCurrentYear(year);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#2F8A92] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="font-hud text-xs text-[#2F8A92] tracking-widest">INITIALIZING NETWORK...</p>
        </div>
      </div>
    );
  }

  if (!user || user === false) return <AuthPage />;

  const renderView = () => {
    // If a chain is selected, show the content page
    if (activeChain) {
      return (
        <ContentPage
          chain={activeChain}
          onNavigate={(chain) => setActiveChain(chain)}
          onViewChange={(view) => { setActiveChain(null); setActiveView(view); }}
        />
      );
    }
    switch (activeView) {
      case 'roots': return <MyRootsDemo />;
      case 'guide': return <AIGuide />;
      case 'workcenter': return <WorkCenter />;
      case 'chat': return <TeacherChat />;
      case 'admin': return <AdminPanel onViewChange={setActiveView} />;
      case 'subscription': return <Subscription />;
      case 'accountability': return <AccountabilityPage />;
      case 'methodology-moderation': return <MethodologyModerationPage />;
      case 'profile': return <MyProfileDemo />;
      case 'parental': return <ParentalControls onBack={() => setActiveView('profile')} />;
      case 'country':
      case 'subdivision':
      case 'city':
        return <ScopedGeoView scope={scope} kind={activeView} onChainSelect={(chain) => setActiveChain(chain)} />;
      case 'review-queue': return <ReviewQueue />;
      case 'teacher-apply': return <TeacherApply />;
      case 'lessons-contest': return <LessonsContest onApply={() => setActiveView('teacher-apply')} />;
      case 'integrity-dashboard': return <IntegrityDashboard />;
      case 'my-certificate': return <MyCertificate />;
      case 'about': return <AboutPage />;
      case 'contact': return <ContactPage />;
      case 'pipeline-queue': return <PipelineQueue onOpenChain={(cid) => {
        // Fetch the chain and route into ContentPage
        (async () => {
          try {
            const r = await (await import('./lib/api')).default.get(`/chains/${cid}`);
            setActiveChain(r.data);
            setActiveView('home');
          } catch {
            // already 404 — admin shouldn't ever see this
          }
        })();
      }} />;
      case 'pipeline-ops': return <PipelineOpsDashboard />;
      default: return <SubjectPage subject={activeView} isKidMode={isKidMode} onChainSelect={(chain) => setActiveChain(chain)} />;
    }
  };

  // Apr 29 2026 follow-up #2: Lead's call: "The page feels like your in
  // prison in a cage. It doesn't move." — the prior `h-screen overflow-hidden`
  // wrapper trapped the entire app to a single viewport, so content pages
  // (My Profile, My Roots, About, Contact) couldn't scroll. The world tab
  // (Time Machine) keeps a fixed-height layout because its globe + timeline
  // are pinned by design; everything else scrolls naturally.
  const isFixedWorldView = activeView === 'world' && !activeChain;
  return (
    <div
      className={isFixedWorldView ? 'w-full h-screen overflow-hidden' : 'w-full min-h-screen overflow-x-hidden'}
      style={{ background: '#0A0A0A' }}
    >
      <HUDLayout
        activeView={activeView}
        onViewChange={(view) => { setActiveChain(null); setActiveView(view); }}
        globeNodes={globeNodes}
        globeBeacons={globeBeacons}
        onNodeClick={handleNodeClick}
        currentYear={currentYear}
        onYearChange={handleYearChange}
        timelineEvents={timelineEvents}
        timelineScope={timelineScope}
        onChainSelect={(chain) => setActiveChain(chain)}
        isKidMode={isKidMode}
        onToggleMode={() => setIsKidMode(!isKidMode)}
        soundOn={soundOn}
        onToggleSound={() => setSoundOn(!soundOn)}
        activeEra={activeEra}
        immersive={!!activeChain}
        immersiveTitle={activeChain?.subject}
        onExitImmersive={() => setActiveChain(null)}
        scope={scope}
        onScopeChange={(next) => { setScope(next); setActiveChain(null); }}
        chrome={
          // Apr 28 2026 brief: "On those pages after the pinhole there
          // should be not globe nor timeline. Just the adjusted tabs as
          // I described earlier. Profile pages should not have a globe
          // or timeline." The chrome prop drives Row 2 + Row 3 visibility.
          ['profile', 'roots', 'my-certificate', 'about', 'contact', 'workcenter', 'accountability', 'methodology-moderation'].includes(activeView)
            ? 'tabs-only'
            : 'full'
        }
      >
        {renderView()}
      </HUDLayout>

      <AudioHUD />
      <LibrarianMentor />
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/subscription/success" element={<SubscriptionSuccess />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/teacher/verify" element={<TeacherVerify />} />
      <Route path="/certificates/verify/:certId" element={<CertificateVerify />} />
      <Route path="/teachers/:teacherId" element={<TeacherProfile />} />
      <Route path="/welcome" element={<PublicLanding />} />
      <Route path="/demo" element={<DemoChainPage />} />
      <Route path="/chains/the-first-walker" element={<TheFirstWalker />} />
      <Route path="/chains/the-shaped-stone" element={<TheShapedStone />} />
      <Route path="/forks/me" element={<MyForks />} />
      <Route path="/forks/:forkId" element={<ForkEditor />} />
      <Route path="/billing" element={<BillingPage />} />
      <Route path="/billing/success" element={<BillingSuccess />} />
      <Route path="/billing/cancel" element={<BillingCancel />} />
      {/* May 01 2026 per Lead: "Also remove intro video for now and link."
          The /intro route is unmounted. Direct navigation to /intro now
          falls through to the catch-all and lands on the landing page.
          Re-enable by uncommenting the line below — FounderOpening +
          CinematicGate components are preserved intact for that path. */}
      {/* <Route path="/intro" element={<CinematicGate force />} /> */}
      <Route path="/login" element={<MainApp />} />
      <Route path="/signup" element={<MainApp />} />
      {/* Apr 29 2026 follow-up #3: Lead's call: "the time machine doesn't
          come up when you press back to time machine. It goes to history
          walked through page." Anonymous visitors clicking BACK TO TIME
          MACHINE on /demo should land on the Time Machine HUD, not get
          bounced back to PublicLanding. Explicit /world route renders
          MainApp for everyone — auth-gated tabs surface a sign-up CTA
          inside the HUD instead of a wholesale redirect. */}
      <Route path="/world" element={<MainApp />} />
      <Route path="*" element={<RootGate />} />
    </Routes>
  );
}

function RootGate() {
  // Authed users go straight in. Unauthed users see cinematic on first visit,
  // landing page on subsequent visits (cinematic skip-on-revisit).
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0A0A' }}>
      <div className="font-mono text-[10px] text-white/35">…</div>
    </div>;
  }
  if (user) return <MainApp />;
  return <CinematicGate />;
}

function CinematicGate({ force = false }) {
  // Apr 30 2026 iter_68 per Lead: "If you can not fix the opening
  // welcome. Lets not waste time or credits. Diagnosis fix or start on
  // history walked through a methodology page." The FounderOpening
  // cinematic is now COMPLETELY SKIPPED — every unauth visitor lands
  // directly on the PublicLanding page (History walked through a
  // methodology that doesn't blink). The `force` flag on /intro is
  // still honored for folks who explicitly want to replay the
  // cinematic (e.g., the "Watch the intro" CTA on the landing page).
  const navigate = useNavigate();
  const [showCinematic, setShowCinematic] = useState(force === true);
  if (showCinematic) {
    return (
      <FounderOpening
        onComplete={() => {
          setShowCinematic(false);
          if (force) navigate('/welcome', { replace: true });
        }}
      />
    );
  }
  try { markCinematicSeen(); } catch { /* ignore */ }
  return <PublicLanding />;
}
// `OpeningCinematic` is kept imported for legacy / regression test paths,
// but the new `/intro` route uses `FounderOpening` per the Apr 27 2026 brief.
void OpeningCinematic;

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <OnlineProvider>
            <AppRoutes />
            <OfflineIndicator />
            <ScrollToTop />
          </OnlineProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
